import React from "react";
import { Index } from "react-instantsearch";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import useMustHidePickems from "@/hooks/useMustHidePickems";
import { NoResultsBoundary } from "./components/NoResultsBoundary/NoResultsBoundary";
import { SportsResults } from "./components/SportsResults";
import { MatchResults } from "./components/MatchResults";
import { RacingResults } from "./components/RacingResults";
import { OutrightResults } from "./components/OutrightResults";
import { PageResults } from "./components/PageResults";
import { PickemsResults } from "./components/PickemsResults";
import { CasinoProvidersResults } from "./components/CasinoProvidersResults";
import { CasinoGamesResults } from "./components/CasinoGamesResults";
import { CasinoCategoriesResults } from "@/features/search/components/Results/components/CasinoCategoriesResults";
import * as styles from "./Results.module.scss";

const Results = () => {
  const permissions = usePermissions();
  const { preferences } = usePreferences();
  const { defaultHub } = preferences;

  const canViewMatches =
    permissions.viewSportsMarkets === "GRANTED" ||
    permissions.viewEsportsMarkets === "GRANTED";

  const canViewRacing = permissions.viewRacingMarkets === "GRANTED";
  const canViewCasino = permissions.viewCasino === "GRANTED";
  const mustHidePickems = useMustHidePickems();
  const canViewPickems = !mustHidePickems;

  const racing = canViewRacing ? (
    <Index key="races-index" indexName={`races`}>
      <RacingResults />
    </Index>
  ) : null;

  const matches = canViewMatches ? (
    <Index key="matches-index" indexName={`matches`}>
      <MatchResults />
    </Index>
  ) : null;

  const outrights = canViewMatches ? (
    <Index key="outrights-index" indexName={`outrights`}>
      <OutrightResults />
    </Index>
  ) : null;

  const sports = (
    <Index key="sports-index" indexName={`sports`}>
      <SportsResults />
    </Index>
  );

  const pickems = canViewPickems ? (
    <Index key="pickems-index" indexName={`pickems`}>
      <PickemsResults />
    </Index>
  ) : null;

  const casinoCategories = canViewCasino ? (
    <Index key="casino-categories-index" indexName={`casino_categories`}>
      <CasinoCategoriesResults />
    </Index>
  ) : null;

  const casinoProviders = canViewCasino ? (
    <Index key="casino-providers-index" indexName={`casino_providers`}>
      <CasinoProvidersResults />
    </Index>
  ) : null;

  const casinoGames = canViewCasino ? (
    <Index key="casino-games-index" indexName={`casino_games`}>
      <CasinoGamesResults />
    </Index>
  ) : null;

  const pages = (
    <Index key="pages-index" indexName={`pages`}>
      <PageResults />
    </Index>
  );

  const resultsOrder: Record<string, any[]> = {
    CASINO: [
      casinoCategories,
      casinoProviders,
      casinoGames,
      racing,
      matches,
      outrights,
      sports,
    ],
    RACING: [
      racing,
      matches,
      outrights,
      sports,
      casinoCategories,
      casinoProviders,
      casinoGames,
    ],
    DEFAULT: [
      matches,
      outrights,
      sports,
      racing,
      casinoCategories,
      casinoProviders,
      casinoGames,
    ],
  };

  const orderedResults = [
    ...((defaultHub && resultsOrder[defaultHub]) || resultsOrder.DEFAULT),
    pickems,
    pages,
  ].filter(Boolean);

  return (
    <NoResultsBoundary>
      <div className={styles.resultsContainer}>{orderedResults}</div>
    </NoResultsBoundary>
  );
};

export { Results };
